import { usePrivy, WalletWithMetadata } from '@privy-io/react-auth';
import { ChevronDown, Link, UserCog2 } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePrivyAccount } from '@/hooks';
import useFundedAccount from '@/hooks/use-funded-account';
import { useAccountStore } from '@/store/use-account-store';
import { useModalStore } from '@/store/use-modal-store';
import { useSettingsStore } from '@/store/use-settings-store';
import { CapitalStatus } from '@/types';
import { cn } from '@/utils/cn';
import { truncateAddress } from '@/utils/format';

type Props = {
  selectOnly?: boolean;
};

export default function AccountSelect({ selectOnly }: Props) {
  const { user } = usePrivy();
  const { isStatusLoading } = useFundedAccount();
  const {
    address: primaryAddress,
    isPrimaryEmbedded,
    hasEmail,
    email,
  } = usePrivyAccount();

  const address = useAccountStore((state) => state.address);
  const capitalStatusByAddress = useAccountStore(
    useShallow((state) => state.capitalStatusByAddress),
  );

  const hideInactiveAccounts = useSettingsStore(
    (state) => state.hideInactiveAccounts,
  );

  const getIsActive = useCallback(
    (address: string) => {
      if (address === primaryAddress) {
        return true;
      }

      switch (capitalStatusByAddress[address.toLowerCase()]) {
        case CapitalStatus.NOT_ELIGIBLE:
          return !isPrimaryEmbedded;
        case CapitalStatus.CREATED:
        case CapitalStatus.PAID:
        case CapitalStatus.PRE_EVAL:
        case CapitalStatus.PRE_FUNDED:
        case CapitalStatus.FUNDED_CLAIMED:
          return false;
        case CapitalStatus.FUNDED_FAILED_MAX_DAILY_LOSS:
        case CapitalStatus.FUNDED_FAILED_MAX_DRAWDOWN:
        case CapitalStatus.GOAL_FAILED_MAX_DAILY_LOSS:
        case CapitalStatus.GOAL_FAILED_MAX_DRAWDOWN:
          return !hideInactiveAccounts;
        default:
          return true;
      }
    },
    [
      primaryAddress,
      capitalStatusByAddress,
      hideInactiveAccounts,
      isPrimaryEmbedded,
    ],
  );

  const wallets = useMemo(() => {
    if (!user?.linkedAccounts) return [];
    return (
      user.linkedAccounts.filter(
        (acc) => acc.type === 'wallet' && getIsActive(acc.address),
      ) as WalletWithMetadata[]
    ).sort((a, b) => {
      if (a.walletClientType === 'privy' && b.walletClientType !== 'privy')
        return 1;
      if (a.walletClientType !== 'privy' && b.walletClientType === 'privy')
        return -1;
      return 0;
    });
  }, [user?.linkedAccounts, getIsActive]);

  if (!user || (selectOnly && wallets.length <= 1)) return;

  const shouldDisplayDropdown =
    (hasEmail || wallets.length <= 1) && !selectOnly;

  return (
    <div className="flex items-center gap-6">
      {wallets.length > 1 && (
        <div
          className={cn(
            'items-center gap-3',
            selectOnly
              ? 'flex w-full'
              : cn(shouldDisplayDropdown && 'hidden sm:flex lg:hidden xl:flex'),
          )}
        >
          {hasEmail && !selectOnly && (
            <p className="text-center text-sm">Account:</p>
          )}
          <div className={cn('flex h-10', selectOnly && 'w-full')}>
            <Select
              disabled={isStatusLoading}
              value={address}
              onValueChange={(value) =>
                useAccountStore.getState().setAddress(value)
              }
            >
              <SelectTrigger className="h-full min-w-max rounded-none bg-muted">
                <SelectValue placeholder="Select account" />
              </SelectTrigger>
              <SelectContent>
                {wallets.map((acc) => (
                  <AccountOption {...acc} key={acc.address} />
                ))}
              </SelectContent>
            </Select>
            {!hasEmail && !selectOnly && (
              <button
                className="flex aspect-square h-full items-center justify-center border border-l-0 border-border bg-muted"
                onClick={() =>
                  useModalStore.setState({ isAccountModalOpen: true })
                }
              >
                <UserCog2 size={14} />
              </button>
            )}
          </div>
        </div>
      )}
      {shouldDisplayDropdown && (
        <button
          className="flex items-center gap-2 text-primary"
          onClick={() => useModalStore.setState({ isAccountModalOpen: true })}
        >
          {hasEmail
            ? email ?? truncateAddress(address as string, 6)
            : truncateAddress(address as string, 6)}
          <ChevronDown size={14} className="text-vestgrey-200" />
        </button>
      )}
    </div>
  );
}

const COLOR_BY_STATUS: Partial<Record<CapitalStatus, string>> = {
  [CapitalStatus.FUNDED]: 'rgb(60 179 113)',
  [CapitalStatus.PENDING_PAYMENT]: 'hsl(var(--yellow))',
  [CapitalStatus.ACTIVE]: 'hsl(var(--yellow))',
  [CapitalStatus.PRE_FUNDED]: 'rgb(60 179 113)',
  [CapitalStatus.GOAL_PASSED]: 'rgb(60 179 113)',
  [CapitalStatus.GOAL_FAILED_MAX_DAILY_LOSS]: 'rgb(243 43 70)',
  [CapitalStatus.GOAL_FAILED_MAX_DRAWDOWN]: 'rgb(243 43 70)',
  [CapitalStatus.FUNDED_FAILED_MAX_DAILY_LOSS]: 'rgb(243 43 70)',
  [CapitalStatus.FUNDED_FAILED_MAX_DRAWDOWN]: 'rgb(243 43 70)',
};

const LABEL_BY_STATUS: Partial<Record<CapitalStatus, string>> = {
  [CapitalStatus.FUNDED]: 'Funded',
  [CapitalStatus.ACTIVE]: 'Eval',
  [CapitalStatus.PENDING_PAYMENT]: 'Pending',
  [CapitalStatus.PRE_FUNDED]: 'Passed',
  [CapitalStatus.GOAL_PASSED]: 'Passed',
  [CapitalStatus.GOAL_FAILED_MAX_DAILY_LOSS]: 'Failed',
  [CapitalStatus.GOAL_FAILED_MAX_DRAWDOWN]: 'Failed',
  [CapitalStatus.FUNDED_FAILED_MAX_DAILY_LOSS]: 'Failed',
  [CapitalStatus.FUNDED_FAILED_MAX_DRAWDOWN]: 'Failed',
};

const AccountOption = ({ address, walletClientType }: WalletWithMetadata) => {
  const capitalStatus = useAccountStore((state) =>
    state.getCapitalStatus(address),
  );
  return (
    <SelectItem
      className="border-b py-2.5 last:border-b-0"
      value={address}
      disabled={capitalStatus === CapitalStatus.PENDING_PAYMENT}
      key={address}
    >
      {walletClientType === 'privy' &&
        (capitalStatus in COLOR_BY_STATUS ? (
          <>
            <div
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: COLOR_BY_STATUS[capitalStatus] }}
            />
            <span className="uppercase">{LABEL_BY_STATUS[capitalStatus]}</span>
          </>
        ) : (
          <Link size={12} />
        ))}
      {truncateAddress(address)}
    </SelectItem>
  );
};
